import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";

type Phase = "verify" | "form" | "success";

const FORMAT_OPTIONS = [
  "1:1 온라인 첨삭",
  "소규모 워크샵",
  "정기 강의 (1개월 과정)",
  "네트워킹 이벤트",
];

const RATING_LABEL: Record<string, string> = {
  "1": "별로예요", "1.5": "별로예요",
  "2": "아쉬워요", "2.5": "아쉬워요",
  "3": "괜찮아요", "3.5": "괜찮아요",
  "4": "좋아요",   "4.5": "좋아요",
  "5": "최고예요!",
};

// ── 반별점 selector ──────────────────────────────────────────────────────────
const HalfStarSelector: React.FC<{
  label: string;
  subLabel?: string;
  value: number;
  onChange: (v: number) => void;
}> = ({ label, subLabel, value, onChange }) => {
  const [hovered, setHovered] = useState(0);
  const display = hovered || value;

  const getFill = (star: number): 0 | 50 | 100 => {
    if (display >= star) return 100;
    if (display >= star - 0.5) return 50;
    return 0;
  };

  return (
    <div>
      <p className="text-sm font-semibold text-[#0A1A3E] mb-0.5">
        {label} <span className="text-red-500">*</span>
      </p>
      {subLabel && <p className="text-xs text-gray-400 mb-2">{subLabel}</p>}
      <div className="flex items-center gap-0.5" onMouseLeave={() => setHovered(0)}>
        {[1, 2, 3, 4, 5].map((star) => {
          const fill = getFill(star);
          return (
            <div key={star} className="relative" style={{ width: 38, height: 38 }}>
              <span
                className="absolute inset-0 text-[38px] text-gray-200 select-none leading-none"
                style={{ lineHeight: "38px" }}
              >★</span>
              {fill > 0 && (
                <span
                  className="absolute inset-0 text-[38px] text-[#FFCF2E] select-none leading-none overflow-hidden"
                  style={{ lineHeight: "38px", width: `${fill}%` }}
                >★</span>
              )}
              <div
                className="absolute left-0 top-0 h-full cursor-pointer"
                style={{ width: "50%" }}
                onMouseEnter={() => setHovered(star - 0.5)}
                onClick={() => onChange(star - 0.5)}
              />
              <div
                className="absolute right-0 top-0 h-full cursor-pointer"
                style={{ width: "50%" }}
                onMouseEnter={() => setHovered(star)}
                onClick={() => onChange(star)}
              />
            </div>
          );
        })}
        <span className="ml-2 text-sm font-bold text-[#0A1A3E] min-w-[120px]">
          {display > 0 ? `${display.toFixed(1)}점 — ${RATING_LABEL[String(display)] ?? ""}` : ""}
        </span>
      </div>
    </div>
  );
};

// ── 메인 컴포넌트 ─────────────────────────────────────────────────────────────
const ReviewFormPage: React.FC = () => {
  const navigate = useNavigate();
  const isDev = process.env.NODE_ENV === "development";

  interface CustomerService {
    id: string;
    product_id: string;
    name_ko: string | null;
    name_en: string | null;
  }
  const [customerServices, setCustomerServices] = useState<CustomerService[]>([]);

  const [phase, setPhase] = useState<Phase>("verify");

  // Verify
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [verifyError, setVerifyError] = useState("");
  const [verifying, setVerifying] = useState(false);

  // Form
  const [serviceId, setServiceId] = useState("");
  const [serviceType, setServiceType] = useState("");
  const [experience, setExperience] = useState("");
  const [satisfactionRating, setSatisfactionRating] = useState(0);
  const [recommendRating, setRecommendRating] = useState(0);
  const [headline, setHeadline] = useState("");
  const [bestPart, setBestPart] = useState("");
  const [improvement, setImprovement] = useState("");
  const [selectedFormats, setSelectedFormats] = useState<string[]>([]);
  const [otherFormat, setOtherFormat] = useState("");
  const [wantsPackage, setWantsPackage] = useState(false);

  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const hasFormatSelected = selectedFormats.length > 0 || otherFormat.trim().length > 0;

  const toggleFormat = (fmt: string) =>
    setSelectedFormats((prev) =>
      prev.includes(fmt) ? prev.filter((f) => f !== fmt) : [...prev, fmt]
    );

  const buildPreferredFormat = (): string | null => {
    const parts = [...selectedFormats];
    if (otherFormat.trim()) parts.push(`기타: ${otherFormat.trim()}`);
    return parts.length > 0 ? parts.join(", ") : null;
  };

  const needsServiceSelection = customerServices.length > 0;
  const canSubmit =
    !submitting &&
    (!needsServiceSelection || serviceId !== "") &&
    experience.trim().length > 0 &&
    satisfactionRating > 0 &&
    recommendRating > 0 &&
    headline.trim().length > 0 &&
    bestPart.trim().length > 0 &&
    improvement.trim().length > 0 &&
    hasFormatSelected;

  // ── Verify ────────────────────────────────────────────────────────────────
  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setVerifyError("");
    setVerifying(true);
    try {
      const res = await fetch("/api/submit-review", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), action: "validate" }),
      });
      if (res.status === 403) {
        setVerifyError(
          "이름과 이메일이 등록된 고객 정보와 일치하지 않습니다.\n서비스 신청 시 사용한 이름과 이메일을 정확히 입력해주세요."
        );
        return;
      }
      if (!res.ok) {
        setVerifyError("확인 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.");
        return;
      }
      const data = await res.json();
      setCustomerServices(data.services ?? []);
      setPhase("form");
    } catch {
      setVerifyError("네트워크 오류가 발생했습니다. 인터넷 연결을 확인해주세요.");
    } finally {
      setVerifying(false);
    }
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError("");

    if (!experience.trim()) {
      setSubmitError("배경을 입력해주세요.");
      return;
    }
    if (satisfactionRating === 0 || recommendRating === 0) {
      setSubmitError("만족도와 추천 의향 별점을 모두 선택해주세요.");
      return;
    }
    if (headline.trim().length < 5) {
      setSubmitError("후기 제목을 5자 이상 입력해주세요.");
      return;
    }
    if (bestPart.trim().length < 10) {
      setSubmitError("가장 만족스러웠던 부분을 10자 이상 작성해주세요.");
      return;
    }
    if (improvement.trim().length < 10) {
      setSubmitError("보완이 필요한 부분을 10자 이상 작성해주세요.");
      return;
    }
    if (!hasFormatSelected) {
      setSubmitError("선호하는 서비스 형식을 하나 이상 선택해주세요.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch("/api/submit-review", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          service_id: serviceId || null,
          service_type: serviceType || null,
          experience: experience.trim() || null,
          satisfaction_rating: satisfactionRating,
          recommend_rating: recommendRating,
          headline: headline.trim(),
          best_part: bestPart.trim(),
          improvement: improvement.trim(),
          preferred_format: buildPreferredFormat(),
          wants_package: wantsPackage,
        }),
      });

      if (res.status === 403) {
        setSubmitError("고객 정보 확인에 실패했습니다. 페이지를 새로고침 후 다시 시도해주세요.");
        return;
      }
      if (res.status === 409) {
        setSubmitError("이미 동일한 서비스에 대한 후기를 제출하셨습니다.");
        return;
      }
      if (!res.ok) {
        setSubmitError("제출 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.");
        return;
      }
      setPhase("success");
    } catch {
      setSubmitError("네트워크 오류가 발생했습니다. 인터넷 연결을 확인해주세요.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-16 px-4">
      <Helmet>
        <title>후기 작성하기 — BeMe Consulting</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <p className="text-sm font-semibold text-[#FFCF2E] bg-[#0A1A3E] inline-block px-3 py-1 rounded-full mb-4 tracking-widest uppercase">
            BeMe Consulting
          </p>
          <h1 className="text-3xl font-extrabold text-[#0A1A3E]">후기 작성하기</h1>
          {phase === "verify" && (
            <p className="mt-3 text-gray-500 text-sm leading-relaxed">
              BeMe 서비스를 이용하신 분만 후기를 작성할 수 있습니다.
              <br />
              서비스 신청 시 사용한 이름과 이메일을 입력해주세요.
            </p>
          )}
        </div>

        {/* ── Phase 1: Verify ── */}
        {phase === "verify" && (
          <form
            onSubmit={handleVerify}
            className="bg-white rounded-2xl shadow-md px-8 py-10 space-y-5"
          >
            <div>
              <label className="block text-sm font-semibold text-[#0A1A3E] mb-1.5">
                이름 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="홍길동"
                required
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0A1A3E]/20 focus:border-[#0A1A3E]"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#0A1A3E] mb-1.5">
                이메일 <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@email.com"
                required
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0A1A3E]/20 focus:border-[#0A1A3E]"
              />
            </div>
            {verifyError && (
              <p className="text-red-500 text-sm whitespace-pre-line leading-relaxed bg-red-50 rounded-xl px-4 py-3">
                {verifyError}
              </p>
            )}
            <button
              type="submit"
              disabled={verifying || !name.trim() || !email.trim()}
              className="w-full bg-[#0A1A3E] text-white font-bold py-3.5 rounded-xl hover:bg-[#11285E] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {verifying ? "확인 중..." : "확인하기 →"}
            </button>
          </form>
        )}

        {/* ── Phase 2: Review Form ── */}
        {phase === "form" && (
          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl shadow-md px-8 py-10 space-y-8"
          >
            {!isDev && (
              <div className="flex items-center gap-2 bg-green-50 text-green-700 rounded-xl px-4 py-2.5 text-sm font-medium">
                <span>✓</span>
                <span>고객 확인 완료 — {name}</span>
              </div>
            )}

            {/* ① 디지털 패키지 */}
            <div className="bg-[#FFCF2E]/10 border border-[#FFCF2E]/50 rounded-2xl px-5 py-5">
              <label className="flex items-start gap-3 cursor-pointer" onClick={() => setWantsPackage((p) => !p)}>
                <div
                  className={`w-5 h-5 mt-0.5 rounded-md border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
                    wantsPackage ? "bg-[#0A1A3E] border-[#0A1A3E]" : "border-gray-400"
                  }`}
                >
                  {wantsPackage && (
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#0A1A3E]">
                    🎁 후기 감사 선물 받기
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">
                    후기 제출 감사의 뜻으로 BeMe LinkedIn 최적화 가이드를 이메일로 보내드립니다.
                  </p>
                </div>
              </label>
            </div>

            {/* ② 서비스 */}
            {customerServices.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-[#0A1A3E] mb-2">
                  후기를 작성할 서비스를 선택해주세요 <span className="text-red-500">*</span>
                </p>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {customerServices.map((svc) => (
                    <button
                      key={svc.id}
                      type="button"
                      onClick={() => { setServiceId(svc.id); setServiceType(svc.product_id); }}
                      className={`text-sm py-2.5 px-3 rounded-xl border font-medium transition-colors ${
                        serviceId === svc.id
                          ? "bg-[#0A1A3E] text-white border-[#0A1A3E]"
                          : "border-gray-200 text-gray-600 hover:border-[#0A1A3E]/40"
                      }`}
                    >
                      {svc.name_ko ?? svc.name_en}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ② 배경 */}
            <div>
              <label className="block text-sm font-semibold text-[#0A1A3E] mb-1.5">
                배경 <span className="text-red-500">*</span>
                <span className="text-xs text-gray-400 font-normal ml-1">— 웹사이트에 표시됩니다</span>
              </label>
              <input
                type="text"
                value={experience}
                onChange={(e) => setExperience(e.target.value)}
                placeholder="예: Product Manager (5 yrs) London, UCL MA 졸업"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0A1A3E]/20 focus:border-[#0A1A3E]"
              />
            </div>

            {/* ③ 만족도 */}
            <HalfStarSelector
              label="서비스에 얼마나 만족하셨나요?"
              subLabel="전반적인 서비스 만족도를 선택해주세요"
              value={satisfactionRating}
              onChange={setSatisfactionRating}
            />

            {/* ④ 추천 의향 */}
            <HalfStarSelector
              label="지인에게 얼마나 추천하고 싶으신가요?"
              subLabel="가까운 지인에게 BeMe를 추천할 의향을 선택해주세요"
              value={recommendRating}
              onChange={setRecommendRating}
            />

            {/* ⑤ 후기 제목 */}
            <div>
              <label className="block text-sm font-semibold text-[#0A1A3E] mb-1.5">
                후기 제목 <span className="text-red-500">*</span>
                <span className="text-xs text-gray-400 font-normal ml-2">({headline.length}/80)</span>
              </label>
              <input
                type="text"
                value={headline}
                onChange={(e) => setHeadline(e.target.value.slice(0, 80))}
                placeholder="예: CV 작성 기준이 생겼어요!"
                required
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0A1A3E]/20 focus:border-[#0A1A3E]"
              />
            </div>

            {/* ⑥ 가장 만족스러웠던 부분 */}
            <div>
              <label className="block text-sm font-semibold text-[#0A1A3E] mb-1">
                피드백을 받고 가장 만족스러웠던 부분은 무엇인가요?{" "}
                <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-gray-400 mb-2">
                예시: 구체적인 문장 제안, 영국 현지 시각, 빠른 답변, 전체적인 논리 구조 등
              </p>
              <textarea
                value={bestPart}
                onChange={(e) => setBestPart(e.target.value)}
                placeholder="자유롭게 작성해주세요"
                rows={3}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0A1A3E]/20 focus:border-[#0A1A3E] resize-none leading-relaxed"
              />
              <p className={`text-xs mt-1 ${bestPart.trim().length >= 10 ? "text-green-500" : "text-gray-400"}`}>
                {bestPart.trim().length} / 최소 10자
              </p>
            </div>

            {/* ⑦ 개선 의견 */}
            <div>
              <label className="block text-sm font-semibold text-[#0A1A3E] mb-1">
                앞으로 이 서비스가 더 좋아지려면 어떤 점이 보완되면 좋을까요?{" "}
                <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-gray-400 mb-2">
                예: 피드백 속도, 더 많은 예시, 단계별 가이드, 포맷 제공 등
              </p>
              <textarea
                value={improvement}
                onChange={(e) => setImprovement(e.target.value)}
                placeholder="솔직한 의견을 들려주세요"
                rows={3}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0A1A3E]/20 focus:border-[#0A1A3E] resize-none leading-relaxed"
              />
              <p className={`text-xs mt-1 ${improvement.trim().length >= 10 ? "text-green-500" : "text-gray-400"}`}>
                {improvement.trim().length} / 최소 10자
              </p>
            </div>

            {/* ⑧ 선호 서비스 형식 */}
            <div>
              <p className="text-sm font-semibold text-[#0A1A3E] mb-1">
                앞으로 어떤 형식으로 서비스를 제공하면 좋을까요?{" "}
                <span className="text-red-500">*</span>
              </p>
              <p className="text-xs text-gray-400 mb-3">복수 선택 가능</p>
              <div className="space-y-2">
                {FORMAT_OPTIONS.map((fmt) => (
                  <label
                    key={fmt}
                    className="flex items-center gap-3 cursor-pointer group"
                    onClick={() => toggleFormat(fmt)}
                  >
                    <div
                      className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
                        selectedFormats.includes(fmt)
                          ? "bg-[#0A1A3E] border-[#0A1A3E]"
                          : "border-gray-300 group-hover:border-[#0A1A3E]/50"
                      }`}
                    >
                      {selectedFormats.includes(fmt) && (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span className="text-sm text-gray-700">{fmt}</span>
                  </label>
                ))}
                {/* 기타 */}
                <div className="flex items-start gap-3">
                  <div
                    className={`w-5 h-5 mt-2.5 rounded-md border-2 flex items-center justify-center transition-colors flex-shrink-0 cursor-pointer ${
                      otherFormat.trim()
                        ? "bg-[#0A1A3E] border-[#0A1A3E]"
                        : "border-gray-300"
                    }`}
                  >
                    {otherFormat.trim() && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1">
                    <span className="text-sm text-gray-700 block mb-1">기타 아이디어 (직접 입력)</span>
                    <input
                      type="text"
                      value={otherFormat}
                      onChange={(e) => setOtherFormat(e.target.value)}
                      placeholder="자유롭게 입력해주세요"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0A1A3E]/20 focus:border-[#0A1A3E]"
                    />
                  </div>
                </div>
              </div>
            </div>

            {submitError && (
              <p className="text-red-500 text-sm bg-red-50 rounded-xl px-4 py-3">{submitError}</p>
            )}

            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full bg-[#0A1A3E] text-white font-bold py-3.5 rounded-xl hover:bg-[#11285E] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? "제출 중..." : "후기 제출하기 →"}
            </button>

            <p className="text-xs text-gray-400 text-center leading-relaxed">
              제출된 후기는 검토 후 웹사이트에 게재됩니다.
            </p>
          </form>
        )}

        {/* ── Phase 3: Success ── */}
        {phase === "success" && (
          <div className="bg-white rounded-2xl shadow-md px-8 py-14 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-extrabold text-[#0A1A3E] mb-3">
              후기가 제출되었습니다!
            </h2>
            <p className="text-gray-500 text-sm leading-relaxed mb-8">
              소중한 후기 감사합니다. 검토 후 웹사이트에 게재됩니다.
              {wantsPackage && (
                <>
                  <br />
                  <span className="text-[#0A1A3E] font-medium">
                    LinkedIn 가이드가 곧 이메일로 발송됩니다 🎁
                  </span>
                </>
              )}
              <br />
              BeMe와 함께한 여정을 공유해주셔서 감사해요 :)
            </p>
            <button
              onClick={() => navigate("/")}
              className="bg-[#0A1A3E] text-white font-bold px-8 py-3 rounded-xl hover:bg-[#11285E] transition-colors"
            >
              홈으로 돌아가기
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewFormPage;
